/**
 * `/onboarding`, the web onboarding container.
 *
 * The step lives in `?step=`, not a path segment, per the URL-as-state
 * rule, matching `/sign-in?step=email`.
 *
 * The server's `onboardingStep` names the furthest step the fan may view,
 * not the step they must be on: it is a ceiling, not a destination. A
 * requested step at or behind that ceiling renders exactly as asked, so
 * browser Back works; only a step strictly ahead of the ceiling, or a
 * missing step, is replaced with the ceiling. The ceiling is raised off the
 * claim and update RESPONSES rather than a `/profile/get` re-read, because
 * that read is briefly stale (both mutations invalidate it on a debounce),
 * and every value the ceiling is built from still comes from the server.
 *
 * This route is the only thing that navigates out of /onboarding. A step
 * reports completion by raising the ceiling; the exit destination is always
 * `/scores`, the landing screen `information-architecture.md` names. It
 * never navigates itself. The leagues finish also
 * seeds the shared profile cache from /profile/update's own response so the
 * root gate does not read a pre-mutation profile the instant the fan leaves.
 */
import { useProfileGet } from "@sportsmet/profile-sdk/private";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { safeRedirectPath } from "../auth/safeRedirectPath";
import { extractErrorCode, showApiError } from "../errors/apiErrorBus";
import {
  AboutYouStep,
  type typeAboutYouForm,
} from "../features/onboarding/AboutYouStep";
import { LeaguesStep } from "../features/onboarding/LeaguesStep";
import {
  furthestServerStep,
  ONBOARDING_PATH,
  resolveOnboardingPlacement,
  resolveOnboardingStepTotal,
  resolveVisibleStepIndex,
} from "../features/onboarding/onboardingSteps";
import { PhotoStep } from "../features/onboarding/PhotoStep";
import { SignInMethodsStep } from "../features/onboarding/SignInMethodsStep";
import { SportsStep } from "../features/onboarding/SportsStep";
import { UsernameStep } from "../features/onboarding/UsernameStep";
import { useSignInMethodOffers } from "../features/onboarding/useSignInMethodOffers";
import { useAvatarUpload } from "../features/profile/useAvatarUpload";
import { validateOnboardingSearch } from "../routeState/onboardingSearch";
import { RoutePendingScreen } from "../shell/RoutePendingScreen";

const OnboardingScreen = (): ReactNode => {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const profileQuery = useProfileGet({});
  const { isPending: areOffersPending, offers } = useSignInMethodOffers();
  const [reachedServerStep, setReachedServerStep] = useState<string | null>(
    null,
  );
  const [stagedSportIds, setStagedSportIds] = useState<string[]>([]);
  const avatarUpload = useAvatarUpload();

  useEffect(() => {
    const code = extractErrorCode(profileQuery.data);
    if (code !== null)
      showApiError(profileQuery.data, undefined, profileQuery.dataUpdatedAt);
  }, [profileQuery.dataUpdatedAt]);

  const profile = profileQuery.data?.data?.profile ?? null;
  const hasSignInMethodsStep = offers.length > 0;
  const stepTotal = resolveOnboardingStepTotal(hasSignInMethodsStep);

  const placement =
    profileQuery.data === undefined || areOffersPending
      ? null
      : resolveOnboardingPlacement({
          profile,
          reachedServerStep,
          requestedSlug: search.step,
          hasSignInMethodsStep,
        });
  // The redirect a fan arrived with (a pending invite, or Start a pool from
  // the marketing site) rides along on every step's URL. A step navigation
  // that dropped it sent a new account to Scores instead of where they were
  // going (ISSUE-1101 R3).
  const keepRedirect =
    search.redirect === undefined ? {} : { redirect: search.redirect };
  const placementKind = placement?.kind ?? null;
  const placementSlug =
    placement === null || placement.kind === "leave" ? null : placement.slug;

  useEffect(() => {
    if (placementKind === null || placementKind === "stay") return;
    if (placementKind === "leave") {
      if (search.redirect !== undefined) {
        void navigate({
          href: safeRedirectPath(search.redirect),
          replace: true,
        });
      } else {
        void navigate({ to: "/scores", search: {}, replace: true });
      }
      return;
    }
    if (placementSlug === null) return;
    void navigate({
      to: ONBOARDING_PATH,
      search: { ...keepRedirect, step: placementSlug },
      replace: true,
    });
  }, [placementKind, placementSlug, search.redirect]);

  if (profileQuery.isPending || areOffersPending) {
    return <RoutePendingScreen />;
  }
  if (placement?.kind !== "stay") {
    return <RoutePendingScreen />;
  }

  const stepIndex =
    resolveVisibleStepIndex({ slug: placement.slug, hasSignInMethodsStep }) ??
    1;

  const initialForm: typeAboutYouForm = {
    firstName: profile?.firstName ?? "",
    lastName: profile?.lastName ?? "",
    birthday: profile?.birthday ?? "",
    phone: profile?.phone ?? "",
  };

  return (
    <div data-testid="onboarding-screen">
      {placement.slug === "username" ? (
        <UsernameStep
          stepIndex={stepIndex}
          stepTotal={stepTotal}
          claimedUsername={profile?.username ?? null}
          onClaimed={({ reachedServerStep: reached }) => {
            if (reached !== null) {
              setReachedServerStep((current) =>
                furthestServerStep(current, reached),
              );
            }
            void navigate({
              to: ONBOARDING_PATH,
              search: { ...keepRedirect, step: "about-you" },
              replace: false,
            });
          }}
        />
      ) : placement.slug === "about-you" ? (
        <AboutYouStep
          stepIndex={stepIndex}
          stepTotal={stepTotal}
          initialForm={initialForm}
          nextServerStep={hasSignInMethodsStep ? "signInMethods" : "photo"}
          onSaved={({ reachedServerStep: reached }) => {
            if (reached !== null) {
              setReachedServerStep((current) =>
                furthestServerStep(current, reached),
              );
            }
            void navigate({
              to: ONBOARDING_PATH,
              search: {
                ...keepRedirect,
                step: hasSignInMethodsStep ? "sign-in-methods" : "photo",
              },
              replace: false,
            });
          }}
        />
      ) : placement.slug === "sign-in-methods" ? (
        <SignInMethodsStep
          stepIndex={stepIndex}
          stepTotal={stepTotal}
          offers={offers}
          emailStep={search.emailStep}
          requestId={search.requestId}
          onOpenEmail={() => {
            void navigate({
              to: ONBOARDING_PATH,
              search: {
                ...keepRedirect,
                step: "sign-in-methods",
                emailStep: "add-email",
              },
            });
          }}
          onEmailRequested={({ requestId: newRequestId }) => {
            void navigate({
              to: ONBOARDING_PATH,
              search: {
                ...keepRedirect,
                step: "sign-in-methods",
                emailStep: "code",
                requestId: newRequestId,
              },
            });
          }}
          onEmailConfirmed={() => {
            void navigate({
              to: ONBOARDING_PATH,
              search: { ...keepRedirect, step: "sign-in-methods" },
            });
          }}
          onEmailCancel={() => {
            void navigate({
              to: ONBOARDING_PATH,
              search: { ...keepRedirect, step: "sign-in-methods" },
            });
          }}
          onAdvanced={({ reachedServerStep: reached }) => {
            if (reached !== null) {
              setReachedServerStep((current) =>
                furthestServerStep(current, reached),
              );
            }
            void navigate({
              to: ONBOARDING_PATH,
              search: { ...keepRedirect, step: "photo" },
              replace: false,
            });
          }}
        />
      ) : placement.slug === "photo" ? (
        <PhotoStep
          stepIndex={stepIndex}
          stepTotal={stepTotal}
          displayName={profile?.displayName ?? ""}
          photoUrl={
            avatarUpload.state.kind === "published"
              ? avatarUpload.state.photoUrl
              : (profile?.photoUrl ?? null)
          }
          isUploadAvailable={true}
          hasUploadFailed={avatarUpload.state.kind === "failed"}
          uploadFailedMessage={
            avatarUpload.state.kind === "failed"
              ? avatarUpload.state.message
              : null
          }
          isPhotoPendingReview={avatarUpload.state.kind === "pendingReview"}
          onFileChosen={(file) => {
            avatarUpload.uploadAvatar(file);
          }}
          onAdvanced={({ reachedServerStep: reached }) => {
            if (reached !== null) {
              setReachedServerStep((current) =>
                furthestServerStep(current, reached),
              );
            }
            void navigate({
              to: ONBOARDING_PATH,
              search: { ...keepRedirect, step: "sports" },
              replace: false,
            });
          }}
        />
      ) : placement.slug === "sports" ? (
        <SportsStep
          stepIndex={stepIndex}
          stepTotal={stepTotal}
          initialSportIds={stagedSportIds}
          onContinue={({ selectedSportIds, reachedServerStep: reached }) => {
            setStagedSportIds(selectedSportIds);
            if (reached !== null) {
              setReachedServerStep((current) =>
                furthestServerStep(current, reached),
              );
            }
            void navigate({
              to: ONBOARDING_PATH,
              search: { ...keepRedirect, step: "leagues" },
              replace: false,
            });
          }}
        />
      ) : (
        // Leagues is the last remaining slug once every branch above is excluded.
        <LeaguesStep
          stepIndex={stepIndex}
          stepTotal={stepTotal}
          selectedSportIds={stagedSportIds}
          onFinished={() => {
            setReachedServerStep((current) =>
              furthestServerStep(current, "done"),
            );
          }}
        />
      )}
    </div>
  );
};

export const Route = createFileRoute("/onboarding")({
  beforeLoad: requireSignedIn,
  validateSearch: validateOnboardingSearch,
  component: OnboardingScreen,
});
